// Photos and documents of a store (WEB_PLAN.md §3.9): every tile asks `useFileUrl(row.filePath)`; tiles rendered in
// the same tick are signed in one request, URLs refresh before they expire, and a 400/404 (storage RLS, a file never
// uploaded) shows "File not available". No image transforms on the bucket, so thumbnails are the full JPEGs drawn
// with object-fit and loaded lazily.

import { AudioLines, ExternalLink, FileText, IdCard, ImageOff } from 'lucide-react';
import type { ReactNode } from 'react';
import type { AttachmentPayload } from '@/contracts/models';
import type { DataItem } from '@/data/hooks';
import { useFileUrl } from '@/sync/hooks';
import { Button, Dialog, KeyValueRow, Spinner, cn } from '@/ui';
import { attachmentTitle } from './detailModel';
import { NewerAppBadge } from './NewerAppBadge';

type Attachment = DataItem<AttachmentPayload>;

function Unavailable({ compact = false }: { compact?: boolean }) {
  return (
    <span className="flex size-full flex-col items-center justify-center gap-1 bg-surface-2 p-1 text-center text-fg-subtle">
      <ImageOff aria-hidden className={compact ? 'size-4' : 'size-5'} />
      {!compact && <span className="text-[11px] leading-tight">File not available</span>}
    </span>
  );
}

/** A square photo tile (Files grid, history thumbnails). Clicking opens the viewer. */
export function PhotoTile({
  item,
  onOpen,
  compact = false,
  className,
}: {
  item: Attachment;
  onOpen: (item: Attachment) => void;
  /** 64 px history thumbnail: no caption overlay. */
  compact?: boolean;
  className?: string;
}) {
  const file = useFileUrl(item.filePath ?? null);
  const a = item.value;
  const title = attachmentTitle(a);
  const missing = !item.filePath || file.status === 'missing';
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      aria-label={`Open ${title}`}
      title={title}
      className={cn(
        'relative block aspect-square overflow-hidden rounded-tile border border-line bg-surface-2',
        'outline-offset-2 transition-[filter] hover:brightness-95',
        className,
      )}
    >
      {missing ? (
        <Unavailable compact={compact} />
      ) : file.status === 'ready' || (file.status === 'loading' && file.url) ? (
        <img src={file.url!} alt="" loading="lazy" decoding="async" className="size-full object-cover" />
      ) : (
        <span aria-hidden className="block size-full animate-shimmer bg-surface-3" />
      )}
      {!compact && a.caption.trim() !== '' && (
        <span className="absolute bottom-1 left-1 max-w-[calc(100%-8px)] truncate rounded-full bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          {a.caption}
        </span>
      )}
      {a.kind === 'businessCard' && (
        <span className="absolute top-1 right-1 rounded-full bg-black/50 p-1 text-white" title="Business card">
          <IdCard aria-hidden className="size-3" />
        </span>
      )}
      {item.undecodable !== undefined && !compact && <NewerAppBadge className="absolute top-1 left-1" />}
    </button>
  );
}

/** A document (or audio note) with an Open link once its URL is signed. */
export function DocumentRow({ item, meta }: { item: Attachment; meta?: ReactNode }) {
  const file = useFileUrl(item.filePath ?? null);
  const a = item.value;
  const title = attachmentTitle(a);
  const Icon = a.kind === 'audio' ? AudioLines : FileText;
  const missing = !item.filePath || file.status === 'missing';
  return (
    <li className="flex items-center gap-3 py-2.5">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-[7px] bg-surface-3 text-accent">
        <Icon aria-hidden className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-medium text-fg">{title}</span>
          {item.undecodable !== undefined && <NewerAppBadge />}
        </span>
        {meta && <span className="block truncate text-xs text-fg-muted">{meta}</span>}
      </span>
      {missing ? (
        <span className="shrink-0 text-xs text-fg-subtle">File not available</span>
      ) : file.status === 'ready' ? (
        <a
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-link hover:underline"
        >
          Open
          <ExternalLink aria-hidden className="size-3.5" />
          <span className="sr-only">{title} (opens in a new tab)</span>
        </a>
      ) : (
        <Spinner size="sm" label={`Preparing ${title}`} />
      )}
    </li>
  );
}

/** Full-size photo with its details (AttachmentDetailSheet, read-only). */
export function PhotoViewer({
  item,
  onClose,
  details,
}: {
  item: Attachment | null;
  onClose: () => void;
  /** Rows under the photo: Added, By, Logged with. */
  details: { label: string; value: ReactNode }[];
}) {
  const file = useFileUrl(item?.filePath ?? null);
  if (!item) return null;
  const title = attachmentTitle(item.value);
  const missing = !item.filePath || file.status === 'missing';
  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          {file.status === 'ready' && (
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mr-auto inline-flex items-center gap-1 text-sm font-semibold text-link hover:underline"
            >
              Open original
              <ExternalLink aria-hidden className="size-3.5" />
            </a>
          )}
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex h-[min(60vh,520px)] items-center justify-center overflow-hidden rounded-tile bg-surface-2">
          {missing ? (
            <Unavailable />
          ) : file.status === 'ready' ? (
            <img src={file.url} alt={item.value.caption || title} className="max-h-full max-w-full object-contain" />
          ) : (
            <Spinner label="Loading photo" />
          )}
        </div>
        <dl className="divide-y divide-line">
          {details.map((d) => (
            <KeyValueRow key={d.label} label={d.label} value={d.value} />
          ))}
        </dl>
      </div>
    </Dialog>
  );
}
